import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Container, Typography, Link as MuiLink } from "@mui/material";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { ThemeContext } from "components/Theme/ThemeContext";
import { useTranslation } from "react-i18next";

export default function TermsAndConditions(props) {
  const { isDark, currentTheme } = useContext(ThemeContext);
  const { i18n } = useTranslation();
  const isRTL = i18n?.language === 'ar' || i18n?.language === 'ara' || (typeof i18n?.language === 'string' && i18n.language.startsWith('ar'));
  const { ...rest } = props;

  return (
    <Box sx={{ bgcolor: currentTheme.background, display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease, color 0.3s ease', direction: isRTL ? 'rtl' : 'ltr' }}>
      <Helmet>
        <title>{isRTL ? 'الشروط والأحكام | زحل قطر' : 'Terms & Conditions | Rahl Qatar'}</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Header
          color="transparent"
          routes={[]}
          rightLinks={<HeaderLinks />}
          fixed
          changeColorOnScroll={{
            height: 100,
            color: isDark ? "dark" : "white"
          }}
          {...rest}
        />

        {/* Hero Section */}
        <Box sx={{
          background: isDark ? 'linear-gradient(135deg, #0A0A0B 0%, #161619 100%)' : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
          color: isDark ? 'white' : '#0F172A',
          pt: 20,
          pb: 8,
          textAlign: 'center'
        }}>
          <Container maxWidth="md">
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, fontFamily: 'Outfit, sans-serif' }}>
              {isRTL ? 'الشروط والأحكام، وسياسة الاسترداد' : 'TERMS & CONDITIONS AND REFUND POLICY'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, color: isDark ? '#94a3b8' : '#475569', fontSize: '0.85rem' }}>
              {isRTL ? 'دولة قطر • تاريخ السريان: 1 أغسطس 2026' : 'STATE OF QATAR JURISDICTION • EFFECTIVE DATE: AUGUST 1, 2026'}
            </Typography>
          </Container>
        </Box>

        {/* Content Section */}
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Box sx={{
            color: isDark ? '#cbd5e1' : '#334155',
            textAlign: isRTL ? 'right' : 'left',
            '& p': { mb: 2.5, lineHeight: 1.8, fontSize: '0.875rem' },
            '& li': { mb: 1, lineHeight: 1.8, fontSize: '0.875rem' },
            '& h3': { mt: 4, mb: 1.5, fontWeight: 700, fontSize: '1.05rem', color: isDark ? '#f8fafc' : '#0f172a', letterSpacing: '0.2px' },
            '& h4': { mt: 3, mb: 1, fontWeight: 700, fontSize: '0.95rem', color: isDark ? '#f8fafc' : '#0f172a' }
          }}>

            {isRTL ? (
              <>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 1 }}>
                  الشروط والأحكام الخاصة بالخدمة
                </Typography>

                <Typography variant="h6" component="h3">
                  1. الاتفاق التعاقدي الملزم
                </Typography>
                <Typography variant="body2">
                  بشرائك أو تفعيلك أو تركيبك أو مسحك لملصق "درع زحل الذكي للسلامة" (Rahl Smart Safety Shield)، أو بدخولك إلى بوابتَي rahl.me أو rahl.inspire.qa، فإنك ("المستخدم" أو "العميل" أو "الماسح") توافق على الالتزام قانونياً بهذه الشروط والأحكام ("الشروط"). في حال عدم موافقتك، يُرجى عدم شراء المنصة أو تفعيلها أو استخدامها.
                </Typography>
                <Typography variant="body2">
                  تشكل هذه الشروط اتفاقية قانونية ملزمة بينك وبين شركة آي ويفز لحلول تكنولوجيا المعلومات (المسجلة في الدوحة، دولة قطر).
                </Typography>

                <Typography variant="h6" component="h3">
                  2. طبيعة المنصة (وسيط تكنولوجي فقط)
                </Typography>
                <Typography variant="body2">
                  توفر زحل منصة تكنولوجية وقناة تواصل سلبية تُيسّر تنبيهات المركبات التبادلية بين الأفراد عبر توجيه وكيل مُقنَّع.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  شركة آي ويفز لحلول تكنولوجيا المعلومات لا تقوم بما يلي:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>العمل كجهة استجابة للطوارئ، أو جهة إنفاذ قانون، أو مزود مساعدة على الطريق، أو خدمة سحب مركبات.</li>
                  <li>التحقق من الدقة الفعلية للتنبيهات المقدَّمة من الماسحين من الأطراف الثالثة.</li>
                  <li>ضمان أن يرى مالك المركبة الإشعار أو المكالمة الواردة أو يقرأها أو يرد عليها أو يتصرف بناءً عليها.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. إخلاء مسؤولية خدمات الطوارئ (إشعار قانوني بالغ الأهمية)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  لا تستخدم زحل في حالات الطوارئ التي تهدد الحياة أو الجرائم النشطة.
                </Typography>
                <Typography variant="body2">
                  زحل هو نظام مجاملة ثانوي غير مخصص للطوارئ. في حال وقوع حوادث مرورية، أو حرائق، أو طوارئ طبية، أو جرائم قيد الوقوع، أو مخاطر سلامة فورية، يجب على المستخدمين والمارّة الاتصال فوراً بخدمات الطوارئ القطرية الرسمية:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li><strong>الطوارئ في قطر:</strong> 999</li>
                  <li><strong>إدارة المرور (قطر):</strong> 993 / 999</li>
                </Box>
                <Typography variant="body2">
                  لا تتحمل شركة آي ويفز لحلول تكنولوجيا المعلومات أي مسؤولية عن أضرار أو إصابات أو وفيات ناتجة عن اختيار أحد الأطراف استخدام منصة زحل بدلاً من الاتصال بخدمات الطوارئ الرسمية.
                </Typography>

                <Typography variant="h6" component="h3">
                  4. التزامات مالك المركبة وضمان الامتثال المروري
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  أ. الامتثال القانوني لموضع تركيب الملصق
                </Typography>
                <Typography variant="body2">
                  يتحمل مالك المركبة المسجل وحده وحصراً مسؤولية الموضع الفعلي لملصق زحل الفينيلي اللاصق الساكن على مركبته.
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li><strong>قواعد الرؤية:</strong> بموجب قانون المرور القطري رقم 19 لسنة 2007 ولوائح المرور الإقليمية في دول مجلس التعاون الخليجي، يجب على سائقي المركبات الحفاظ على مجال رؤية غير معاق عبر الزجاج الأمامي والنوافذ الجانبية للسائق.</li>
                  <li><strong>منطقة التركيب الإلزامية:</strong> يجب تركيب ملصقات زحل على مناطق زجاجية غير حرجة، مثل الزجاج الثابت في الربع الخلفي (يُوصى به) أو الزاوية السفلى لمقعد الراكب الجانبي، بعيداً عن خط رؤية السائق المباشر ومناطق مسح ماسحات الزجاج الأمامي.</li>
                  <li><strong>إخلاء المسؤولية القانونية والتعويض:</strong> تخلي شركة آي ويفز لحلول تكنولوجيا المعلومات صراحةً مسؤوليتها عن أي مخالفات مرورية، أو نقاط سوداء، أو فشل في فحص المركبة (فحص الصلاحية - FAHES)، أو حجز صادر عن وزارة الداخلية / الإدارة العامة للمرور نتيجة سوء تركيب الملصق. ويلتزم مالك المركبة بتعويض شركة آي ويفز لحلول تكنولوجيا المعلومات عن جميع الغرامات أو المطالبات المرتبطة بذلك.</li>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ب. دقة المعلومات
                </Typography>
                <Typography variant="body2">
                  يوافق أصحاب الحسابات على الحفاظ على أرقام هواتف محمولة دقيقة ومحدّثة مرتبطة بالعلامة الخاصة بهم. لا تتحمل شركة آي ويفز لحلول تكنولوجيا المعلومات مسؤولية التنبيهات الموجَّهة بشكل خاطئ بسبب بيانات ملف تعريف مستخدم قديمة.
                </Typography>

                <Typography variant="h6" component="h3">
                  5. سياسة الاستخدام المقبول وسلوك المارّة
                </Typography>
                <Typography variant="body2">
                  يوافق أي مستخدم من الجمهور يمسح ملصق زحل على استخدام النظام حصراً لأغراض السلامة والمجاملة المشروعة المتعلقة بالمركبة (مثل عوائق مواقف السيارات، النوافذ المفتوحة، الأضواء المتروكة مُشغّلة، تنبيهات مخاطر الأطفال/الحيوانات الأليفة).
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  تشمل الاستخدامات المحظورة:
                </Typography>
                <Box component="ol" sx={{ pr: 3, pl: 0 }}>
                  <li>استخدام المنصة لإرسال رسائل/مكالمات مسيئة أو تحرشية أو مبتذلة أو تهديدية.</li>
                  <li>تقديم بلاغات مخاطر مركبات كاذبة أو مزعجة (سبام) أو احتيالية.</li>
                  <li>محاولة الهندسة العكسية، أو فك الشيفرة، أو استخراج البيانات، أو تجاوز نظام التوجيه المُقنَّع أو بنية رمز QR الديناميكي.</li>
                  <li>استخدام النظام لأغراض التسويق التجاري أو الإعلانات أو الرسائل المزعجة أو الرسائل السياسية.</li>
                </Box>
                <Typography variant="body2">
                  تحتفظ شركة آي ويفز لحلول تكنولوجيا المعلومات بالحق في حظر أو إدراج في القائمة السوداء أو إنهاء الوصول فوراً لأي عنوان IP، أو جهاز، أو رقم هاتف يُثبت انتهاكه لهذا القسم، وإبلاغ المركز القطري للأمن السيبراني ومكافحة الجرائم الإلكترونية بأي تحرش جسيم.
                </Typography>

                <Typography variant="h6" component="h3">
                  6. حقوق الملكية الفكرية
                </Typography>
                <Typography variant="body2">
                  جميع حقوق الملكية الفكرية في منصة زحل - بما في ذلك على سبيل المثال لا الحصر العلامة التجارية "زحل" (Rahl)، وشيفرة البرمجيات، وتصاميم واجهة المستخدم، ومنطق توجيه رمز QR الديناميكي، وبنية الاتصال الهاتفي المُقنَّع، والأسرار التجارية، والأعمال الفنية المطبوعة - هي ملكية حصرية لشركة آي ويفز لحلول تكنولوجيا المعلومات.
                </Typography>
                <Typography variant="body2">
                  يُحظر تماماً النسخ أو النقل أو التعديل أو الاستغلال التجاري غير المصرح به للأجهزة أو البرمجيات أو أصول العلامة التجارية، ويخضع لملاحقة مدنية وجنائية بموجب قانون قطر رقم 7 لسنة 2002 (حماية حقوق المؤلف والحقوق المجاورة) وقوانين العلامات التجارية.
                </Typography>

                <Typography variant="h6" component="h3">
                  7. إخلاء المسؤولية عن الضمانات
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  تُقدَّم المنصة وملصقات الأجهزة والخدمات على أساس "كما هي" و"حسب التوفر" دون أي ضمانات من أي نوع، صريحة أو ضمنية.
                </Typography>
                <Typography variant="body2">
                  تخلي شركة آي ويفز لحلول تكنولوجيا المعلومات صراحةً مسؤوليتها عن جميع الضمانات، بما في ذلك على سبيل المثال لا الحصر:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>توجيه اتصالات هاتفية أو تسليم رسائل نصية دون انقطاع أو خطأ أو تأخير.</li>
                  <li>مقاومة الملصقات المادية للتخريب المتعمد، أو الكشط الفعلي، أو التلف الفعلي الشديد.</li>
                  <li>التوفر الفعلي أو الاستجابة أو السلوك الفعلي لمالكي المركبات عند الاتصال بهم.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  8. تحديد المسؤولية
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  إلى أقصى حد يسمح به القانون المعمول به في دولة قطر:
                </Typography>
                <Box component="ol" sx={{ pr: 3, pl: 0 }}>
                  <li><strong>الأضرار غير المباشرة:</strong> لا تتحمل شركة آي ويفز لحلول تكنولوجيا المعلومات أي مسؤولية عن أي أضرار غير مباشرة، أو عرضية، أو تبعية، أو خاصة، أو عقابية، أو نموذجية، بما في ذلك رسوم سحب المركبات، أو مخالفات الوقوف، أو أضرار زجاج المركبة، أو ضياع الوقت، أو النزاعات الشخصية بين السائقين.</li>
                  <li><strong>سقف المسؤولية المباشرة:</strong> في جميع الأحوال، لا يتجاوز إجمالي المسؤولية الكلية لشركة آي ويفز لحلول تكنولوجيا المعلومات عن أي مطالبات ناشئة عن استخدام المنصة أو عدم القدرة على استخدامها المبلغ الفعلي الذي دفعه المستخدم مقابل منتج زحل المحدد المعني (بحد أقصى قدره 50.00 ريال قطري لكل حادثة).</li>
                </Box>

                <Typography variant="h6" component="h3">
                  9. التعويض
                </Typography>
                <Typography variant="body2">
                  توافق على الدفاع عن شركة آي ويفز لحلول تكنولوجيا المعلومات ومديريها ومسؤوليها وموظفيها وشركائها ووكلائها وتعويضهم وإبراء ذمتهم من وضد أي مطالبات، أو التزامات، أو أضرار، أو خسائر، أو تكاليف، أو نفقات (بما في ذلك الأتعاب القانونية) ناشئة عن:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>خرقك لهذه الشروط.</li>
                  <li>انتهاكك لقوانين المرور القطرية أو قوانين خصوصية البيانات.</li>
                  <li>أي نزاع مادي، أو تصادم، أو مشادة، أو حادثة تقع بينك وبين سائق آخر أو أحد المارّة.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  10. القانون الحاكم والاختصاص القضائي
                </Typography>
                <Typography variant="body2">
                  تخضع هذه الشروط وتُفسَّر وتُنفَّذ وفقاً لقوانين دولة قطر.
                </Typography>
                <Typography variant="body2">
                  يخضع أي نزاع قانوني أو دعوى أو إجراء ينشأ عن هذه الشروط أو عن المنصة أو يتعلق بها للاختصاص القضائي الحصري للمحاكم المختصة في مدينة الدوحة، دولة قطر.
                </Typography>

                <Typography variant="h6" component="h3">
                  11. تعديلات النظام، وتحديثات الميزات، وتعديلات الأسعار
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  أ. الحق في تعديل ميزات المنصة والنظام
                </Typography>
                <Typography variant="body2">
                  تحتفظ شركة آي ويفز لحلول تكنولوجيا المعلومات بالحق الكامل وغير المحدود، وفق تقديرها المطلق، في تعديل أو تحديث أو ترقية أو إضافة أو تقييد أو إيقاف أي جانب، أو ميزة، أو بروتوكول تقني، أو واجهة مستخدم، أو بنية نظام لمنصة زحل (بما في ذلك rahl.me، والتطبيقات المتنقلة، وأطر توجيه VoIP المُقنَّعة) في أي وقت، بإشعار مسبق أو دون إشعار، وبموافقة المستخدم الفردية أو دونها.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ب. الحق في تعديل هيكل التسعير
                </Typography>
                <Typography variant="body2">
                  تحتفظ شركة آي ويفز لحلول تكنولوجيا المعلومات بالحق في مراجعة أو تعديل أو زيادة أسعار التجزئة للمنتجات المادية (دروع زحل الذكية للسلامة)، أو معدلات الاشتراك الرقمي السنوي، أو عمولات الشركاء، أو رسوم الخدمة الثانوية، وفق تقديرها المطلق دون الحاجة إلى موافقة مسبقة أو فردية من المستخدم.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ج. تطبيق تعديلات الأسعار
                </Typography>
                <Typography variant="body2">
                  لن تُغيّر مراجعات الأسعار بأثر رجعي أو تؤثر على دورة اشتراك سنوية نشطة ومدفوعة بالكامل وقيد التنفيذ بالفعل. وسيُطبَّق هيكل التسعير المحدَّث تلقائياً على:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>جميع مشتريات الأجهزة الجديدة أو تفعيلات العلامات الثانوية.</li>
                  <li>عمليات تجديد الاشتراك السنوي اليدوية التي تبدأ عند انتهاء مدة الاشتراك الحالية.</li>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  د. استمرار الاستخدام كقبول غير مشروط
                </Typography>
                <Typography variant="body2">
                  يشكل استمرار وصول المستخدم أو العميل إلى منصة زحل أو استخدامه لملصقات الأجهزة أو الخدمات الرقمية بعد أي تحديثات للنظام، أو تعديلات تقنية، أو تعديلات أسعار منشورة، قبولاً كاملاً وملزماً وغير مشروط لتلك التغييرات.
                </Typography>

                {/* Refund / Return / Cancellation Policy */}
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mt: 6, mb: 1 }}>
                  سياسة الاسترداد والإرجاع والإلغاء
                </Typography>

                <Typography variant="h6" component="h3">
                  1. المنتجات المادية (درع زحل الفينيلي اللاصق الساكن)
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  أ. فترة الإرجاع والاستبدال القانونية (المنتجات غير المفتوحة)
                </Typography>
                <Typography variant="body2">
                  عملاً بقانون حماية المستهلك القطري رقم 8 لسنة 2008، يحق للعملاء الحصول على استرداد كامل أو استبدال لعبوات منتجات زحل المادية غير المفتوحة وغير المستخدمة خلال سبعة (7) أيام تقويمية من تاريخ الشراء/التسليم، وذلك وفق الشروط الإلزامية التالية:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>يجب أن تبقى عبوة المنتج مختومة من المصنع في تغليفها الأصلي غير التالف.</li>
                  <li>يجب ألا يكون الملصق الفينيلي اللاصق الساكن قد قُشِّر أو طُبِّق على أي سطح أو سُجِّل/فُعِّل عبر الإنترنت.</li>
                  <li>يجب تقديم الفاتورة الضريبية/الإيصال الأصلي.</li>
                </Box>
                <Typography variant="body2">
                  تُصرف عمليات الاسترداد القانونية المعتمدة بموجب هذا البند إلى وسيلة الدفع الأصلية خلال 7 إلى 14 يوم عمل.
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  ب. حالات عدم استرداد قيمة الجهاز (استرداد 0% صارم)
                </Typography>
                <Typography variant="body2">
                  باستثناء ما يقتضيه القانون القطري بشأن السلع المعيبة، لن تُقبل عمليات الاسترداد أو الإرجاع تحت أي ظرف من الظروف إذا:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>تم تقشير أو تطبيق أو تعديل الورق الواقي الخلفي للملصق الفينيلي اللاصق الساكن.</li>
                  <li>تم تفعيل رمز QR المطبوع على الملصق وربطه بحساب على rahl.me.</li>
                  <li>ظهر على الملصق تلف مادي، أو تمزقات، أو ثنيات، أو تلوث لاصق ناتج عن سوء المناولة، أو مواد التنظيف الكيميائية، أو التركيب غير السليم على الزجاج من قِبل العميل.</li>
                  <li>تم الشراء عبر بائع تجزئة من طرف ثالث غير مصرح له.</li>
                </Box>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  ج. الاستثناءات التقديرية ورصيد المنصة (طلبات غير قانونية إلزامية)
                </Typography>
                <Typography variant="body2">
                  إذا طلب العميل إلغاءً أو إرجاعاً خارج نطاق فترة السبعة أيام القانونية، أو لمنتج لا يستوفي معايير الإرجاع القياسية، فإن شركة آي ويفز لحلول تكنولوجيا المعلومات غير ملزمة قانوناً بإصدار استرداد نقدي.
                </Typography>
                <Typography variant="body2">
                  ومع ذلك، ووفق التقدير الحصري للإدارة، يجوز لشركة آي ويفز لحلول تكنولوجيا المعلومات تقديم تسوية على سبيل حسن النية تتمثل في رمز كوبون رقمي يعادل ما يصل إلى 50% من قيمة الشراء الفعلية. هذا الكوبون:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>صالح حصراً للخدمات الرقمية، أو التمديدات، أو المشتريات الثانوية عبر منصاتنا.</li>
                  <li>غير قابل للتحويل ولا يمكن استبداله أو استرداده أو تحويله إلى نقد أو تحويل بنكي تحت أي ظرف من الظروف.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  2. الخدمات الرقمية والتجديد السنوي وسياسة حذف الحساب
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  أ. حصرية نموذج الفوترة السنوية
                </Typography>
                <Typography variant="body2">
                  يُقدَّم الوصول إلى المنصة، وتوجيه VoIP المُقنَّع، وميزات رمز QR الديناميكي حصراً على أساس اشتراك سنوي مدفوع مسبقاً. خيارات الاشتراك الشهري غير متوفرة.
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  ب. عدم تخزين بيانات بطاقات الدفع والتجديد اليدوي
                </Typography>
                <Typography variant="body2">
                  امتثالاً صارماً لقواعد حماية البيانات المالية الإقليمية، ولوائح مصرف قطر المركزي (QCB)، ومعايير PCI-DSS:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>لا تقوم زحل ولا شركة آي ويفز لحلول تكنولوجيا المعلومات بتخزين أو الاحتفاظ ببيانات اعتماد بطاقات الائتمان أو الخصم أو البيانات المصرفية على خوادم زحل، ولا معالجتها.</li>
                  <li>تُعالَج عمليات الشراء الأولية عبر البوابات الشريكة أو تطبيق زحل من خلال بوابات دفع آمنة ومرخصة من أطراف ثالثة.</li>
                  <li>نظراً لعدم تخزين بيانات البطاقة، فإن الخصم التلقائي مُعطَّل. يجب على صاحب الحساب بدء عمليات التجديد السنوي ودفعها يدوياً مباشرة عبر تطبيق زحل عند انتهاء الدورة السنوية.</li>
                </Box>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  ج. حذف الحساب وقاعدة عدم الاسترداد
                </Typography>
                <Typography variant="body2">
                  يحق للمستخدمين طلب حذف الملف الشخصي أو إنهاء الحساب في أي وقت عبر تطبيق زحل أو لوحة تحكم الحساب. ومع ذلك:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>لا يخوّل حذف الحساب أو الإنهاء المبكر للملف الشخصي المستخدم أي استرداد نقدي كامل أو جزئي أو تناسبي، أو استرداد عبر البنك، أو رصيد متجر عن الأشهر أو الأيام غير المستخدمة المتبقية من فترة الاشتراك السنوي النشطة.
                  </li>
                  <li>عند حذف الملف الشخصي، سيتم إلغاء تفعيل درع السلامة الذكي ورمز QR الديناميكي المرتبط به وفصله عن النظام فوراً لأغراض أمنية.</li>
                </Box>
                <Box component="ol" sx={{ pr: 3, pl: 0 }}>
                  <li><strong>تفعيل الخدمة الرقمية:</strong> رسوم التفعيل الرقمي واشتراكات إدارة المنصة المتكررة المرتبطة بنظام التوجيه المُقنَّع لزحل غير قابلة للاسترداد بمجرد تفعيلها.</li>
                  <li><strong>إلغاء الاشتراك:</strong> يجوز للمستخدمين إلغاء التجديد التلقائي لاشتراكهم في أي وقت عبر لوحة تحكم حساب زحل. يسري الإلغاء عند نهاية دورة الفوترة المدفوعة الحالية. لا تُصرف استردادات جزئية أو تناسبية عن الأيام غير المستخدمة خلال دورة فوترة نشطة.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. المنتجات المعيبة أو التالفة
                </Typography>
                <Typography variant="body2">
                  إذا استلم العميل منتجاً تالفاً مادياً عند التسليم أو يحتوي على رمز QR معيب/غير قابل للمسح قبل التطبيق:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li>يجب على العميل إخطار شريك التوصيل أو دعم عملاء زحل عبر support@rahl.me أو +974 31395694 خلال ثمانٍ وأربعين (48) ساعة من التسليم، مع تقديم أدلة صور/فيديو.</li>
                  <li>بعد التحقق، ستقوم شركة آي ويفز لحلول تكنولوجيا المعلومات أو شريك التوصيل بإصدار منتج بديل مجاني.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  4. طريقة معالجة الاسترداد
                </Typography>
                <Typography variant="body2">
                  تُعالَج عمليات الاسترداد النقدي المعتمدة باستخدام وسيلة الدفع الأصلية المستخدمة أثناء الشراء:
                </Typography>
                <Box component="ul" sx={{ pr: 3, pl: 0 }}>
                  <li><strong>الدفع ببطاقة الائتمان/الخصم:</strong> تُعالَج ويُعاد المبلغ إلى البطاقة المُصدِرة خلال 7 إلى 14 يوم عمل (وفقاً لأوقات معالجة البنك).</li>
                  <li><strong>الدفع عند الاستلام (COD):</strong> يُسترد المبلغ عبر تحويل بنكي أو رمز قسيمة رقمية حسب الاتفاق مع العميل.</li>
                  <li><em>ملاحظة:</em> رسوم الشحن والتوصيل والمناولة غير قابلة للاسترداد ما لم يكن الإرجاع بسبب عيب تصنيع مؤكد أو خطأ في التنفيذ من قِبل شركة آي ويفز لحلول تكنولوجيا المعلومات.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  5. جهة التواصل لخدمة العملاء بخصوص المطالبات
                </Typography>
                <Typography variant="body2">
                  لجميع الإشعارات القانونية، أو استفسارات الخصوصية، أو مطالبات الاسترداد، يُرجى التواصل مع:
                  <br />
                  <strong>شركة آي ويفز لحلول تكنولوجيا المعلومات - دعم زحل</strong>
                  <br />
                  <strong>البريد الإلكتروني:</strong> <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink>
                  <br />
                  <strong>خدمة العملاء:</strong> <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>
                  <br />
                  <strong>العنوان:</strong> الدوحة، دولة قطر
                  <br />
                  <strong>الموقع الإلكتروني:</strong> www.rahl.me
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mt: 1, mb: 1 }}>
                  Terms and Conditions of Service
                </Typography>

                <Typography variant="h6" component="h3">
                  1. Contractual Binding Agreement
                </Typography>
                <Typography variant="body2">
                  By purchasing, activating, applying, or scanning a Rahl Smart Safety Shield vinyl tag, or by accessing the rahl.me or rahl.inspire.qa portals, you ("User," "Client," or "Scanner") agree to be legally bound by these Terms and Conditions ("Terms"). If you do not agree, do not purchase, activate, or use the Platform.
                </Typography>
                <Typography variant="body2">
                  These Terms constitute a binding legal agreement between you and iWaves IT Solution Services (registered in Doha, Qatar).
                </Typography>

                <Typography variant="h6" component="h3">
                  2. Nature of the Platform (Technology Intermediary Only)
                </Typography>
                <Typography variant="body2">
                  Rahl provides a technological platform and passive communication channel facilitating peer-to-peer courtesy vehicle alerts via masked proxy routing.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  iWaves IT Solution Services DOES NOT:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Operate as an emergency response agency, law enforcement body, roadside assistance provider, or vehicle towing service.</li>
                  <li>Verify the physical accuracy of alerts submitted by third-party scanners.</li>
                  <li>Guarantee that a vehicle owner will see, read, answer, or act upon a received notification or phone call.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. Emergency Services Disclaimer (Critical Legal Notice)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Do not use Rahl for life-threatening emergencies or active crimes.
                </Typography>
                <Typography variant="body2">
                  Rahl is a secondary, non-emergency courtesy system. In the event of traffic accidents, fires, medical emergencies, crimes in progress, or immediate safety hazards, users and bystanders MUST immediately contact official Qatari Emergency Services:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li><strong>Qatar Emergency Services:</strong> 999</li>
                  <li><strong>Traffic Department (Qatar):</strong> 993 / 999</li>
                </Box>
                <Typography variant="body2">
                  iWaves IT Solution Services accepts zero liability for damages, injuries, or fatalities resulting from a party's choice to use the Rahl Platform instead of contacting official emergency services.
                </Typography>

                <Typography variant="h6" component="h3">
                  4. Vehicle Owner Obligations &amp; Traffic Compliance Safeguard
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  A. Legal Compliance for Tag Placement
                </Typography>
                <Typography variant="body2">
                  The registered vehicle owner is solely and exclusively responsible for the physical placement of the Rahl static-cling vinyl sticker on their vehicle.
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li><strong>Visibility Rules:</strong> Under Qatar Traffic Law No. 19 of 2007 and regional GCC traffic regulations, vehicle drivers must maintain an unobstructed field of vision through the windshield and driver-side windows.</li>
                  <li><strong>Mandatory Placement Zone:</strong> Rahl stickers MUST be placed on non-critical glass areas, such as the rear quarter fixed window (recommended) or the lowest passenger side corner, clear of the driver's direct line of sight and windshield wiper sweep zones.</li>
                  <li><strong>Legal Disclaimer &amp; Indemnification:</strong> iWaves IT Solution Services explicitly disclaims any liability for traffic fines, black points, vehicle inspection failures (FAHES), or impoundment issued by the Ministry of Interior / General Directorate of Traffic resulting from improper sticker placement. The vehicle owner shall indemnify iWaves IT Solution Services against all associated fines or claims.</li>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  B. Accuracy of Information
                </Typography>
                <Typography variant="body2">
                  Account Holders agree to maintain accurate, current mobile phone numbers linked to their tag. iWaves IT Solution Services is not liable for misdirected alerts caused by outdated user profile details.
                </Typography>

                <Typography variant="h6" component="h3">
                  5. Acceptable Use &amp; Bystander Conduct Policy
                </Typography>
                <Typography variant="body2">
                  Any public user scanning a Rahl tag agrees to utilize the system strictly for legitimate vehicle-related safety and courtesy communications (e.g., parking blockages, open windows, lights left ON, child/pet hazard alerts).
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Prohibited uses include:
                </Typography>
                <Box component="ol" sx={{ pl: 3 }}>
                  <li>Using the platform to send offensive, harassing, abusive, obscene, or threatening messages/calls.</li>
                  <li>Submitting false, spam, or fraudulent vehicle hazard reports.</li>
                  <li>Attempting to reverse-engineer, decompile, scrape, or bypass the Masked Routing System or dynamic QR architecture.</li>
                  <li>Using the system for commercial solicitations, marketing, spam, or political messages.</li>
                </Box>
                <Typography variant="body2">
                  iWaves IT Solution Services reserves the right to immediately block, blacklist, or terminate access for any IP address, device, or phone number found violating this section, and report severe harassment to the Qatari Centre for Cybersecurity &amp; Cybercrime Prevention.
                </Typography>

                <Typography variant="h6" component="h3">
                  6. Intellectual Property Rights
                </Typography>
                <Typography variant="body2">
                  All intellectual property rights in the Rahl platform—including but not limited to the brand mark Rahl, software code, user interface designs, dynamic QR routing logic, masked telephony architecture, trade secrets, and printed artwork—are the exclusive property of iWaves IT Solution Services.
                </Typography>
                <Typography variant="body2">
                  Unauthorized copying, replication, modification, or commercial exploitation of the hardware, software, or brand assets is strictly prohibited and subject to civil and criminal prosecution under Qatar Law No. 7 of 2002 (Protection of Copyright and Neighboring Rights) and Trademark Laws.
                </Typography>

                <Typography variant="h6" component="h3">
                  7. Disclaimer of Warranties
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  The Platform, hardware tags, and services are provided on an "as is" and "as available" basis without warranties of any kind, express or implied.
                </Typography>
                <Typography variant="body2">
                  iWaves IT Solution Services expressly disclaims all warranties, including but not limited to:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Uninterrupted, error-free, or delay-free telecom routing or SMS delivery.</li>
                  <li>Resistance of physical stickers to deliberate vandalism, physical scraping, or extreme physical destruction.</li>
                  <li>The actual availability, responsiveness, or demeanor of vehicle owners when contacted.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  8. Limitation of Liability
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  To the maximum extent permitted by applicable law in the State of Qatar:
                </Typography>
                <Box component="ol" sx={{ pl: 3 }}>
                  <li><strong>Indirect Damages:</strong> iWaves IT Solution Services shall not be liable for any indirect, incidental, consequential, special, punitive, or exemplary damages, including vehicle towing fees, parking fines, damage to vehicle glass, loss of time, or personal disputes between drivers.</li>
                  <li><strong>Cap on Direct Liability:</strong> In all events, the total aggregate liability of iWaves IT Solution Services for any claims arising from the use or inability to use the Platform shall not exceed the actual amount paid by the User for the specific Rahl product in question (cap at max QAR 50.00 per incident).</li>
                </Box>

                <Typography variant="h6" component="h3">
                  9. Indemnification
                </Typography>
                <Typography variant="body2">
                  You agree to defend, indemnify, and hold harmless iWaves IT Solution Services, its directors, officers, employees, partners, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including legal fees) arising from:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Your breach of these Terms.</li>
                  <li>Your violation of Qatari Traffic Laws or Data Privacy laws.</li>
                  <li>Any physical dispute, collision, altercation, or incident occurring between you and another driver or bystander.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  10. Governing Law &amp; Jurisdiction
                </Typography>
                <Typography variant="body2">
                  These Terms shall be governed by, construed, and enforced in accordance with the laws of the State of Qatar.
                </Typography>
                <Typography variant="body2">
                  Any legal dispute, suit, or proceeding arising out of or relating to these Terms or the Platform shall be submitted to the exclusive jurisdiction of the competent Courts of Doha, State of Qatar.
                </Typography>

                <Typography variant="h6" component="h3">
                  11. System Modifications, Feature Updates &amp; Price Adjustments
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  A. Right to Modify Platform &amp; System Features
                </Typography>
                <Typography variant="body2">
                  iWaves IT Solution Services reserves the full, unreserved right, at its sole discretion, to modify, update, upgrade, add, restrict, or discontinue any aspect, feature, technical protocol, user interface, or system architecture of the Rahl Platform (including rahl.me, mobile applications, and masked VoIP routing frameworks) at any time, with or without prior notice or individual user approval.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  B. Right to Adjust Pricing Structure
                </Typography>
                <Typography variant="body2">
                  iWaves IT Solution Services reserves the right to revise, adjust, or increase the retail prices of physical hardware products (Rahl Smart Safety Shields), annual digital subscription rates, partner commissions, or secondary service fees at its sole discretion without requiring prior user consent or individual approval.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  C. Application of Pricing Changes
                </Typography>
                <Typography variant="body2">
                  Price revisions will not retroactively alter or affect an active, fully paid annual subscription cycle already in progress. Any updated pricing structure will automatically apply to:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>All new hardware purchases or secondary tag activations.</li>
                  <li>Manual annual subscription renewals initiated upon the expiration of an existing subscription term.</li>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  D. Continued Use as Unconditional Acceptance
                </Typography>
                <Typography variant="body2">
                  A user's or client's continued access to or use of the Rahl Platform, hardware tags, or digital services following any system updates, technical modifications, or published price adjustments shall constitute full, binding, and unconditional acceptance of those changes.
                </Typography>

                {/* Refund / Return / Cancellation Policy */}
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mt: 6, mb: 1 }}>
                  Refund, Return &amp; Cancellation Policy
                </Typography>

                <Typography variant="h6" component="h3">
                  1. Physical Hardware Products (Rahl Static-Cling Vinyl Shield)
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  A. Statutory Returns &amp; Exchange Window (Unopened Items)
                </Typography>
                <Typography variant="body2">
                  Pursuant to Qatar Consumer Protection Law No. 8 of 2008, customers are entitled to a full refund or exchange on unopened, unused Rahl physical product packages within seven (7) calendar days from the date of purchase/delivery, subject to the following mandatory conditions:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>The product package must remain factory-sealed in its original, undamaged packaging.</li>
                  <li>The static-cling vinyl tag must not have been peeled, applied to any surface, or registered/activated online.</li>
                  <li>Original tax invoice/receipt must be presented.</li>
                </Box>
                <Typography variant="body2">
                  Approved statutory refunds under this subsection will be issued to the original method of payment within 7–14 business days.
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  B. Non-Refundable Hardware Scenarios (Strict 0% Refund)
                </Typography>
                <Typography variant="body2">
                  Except as required by Qatari Law for defective goods, refunds or returns will NOT be accepted under any circumstances if:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>The protective backing paper of the static-cling vinyl sticker has been peeled, applied, or altered.</li>
                  <li>The QR Code printed on the sticker has been activated and linked to an account on rahl.me.</li>
                  <li>The sticker exhibits physical damage, tears, creases, or adhesive contamination resulting from improper handling, cleaning chemicals, or improper glass installation by the customer.</li>
                  <li>The purchase was made through an unauthorized third-party reseller.</li>
                </Box>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  C. Discretionary Exceptions &amp; Platform Credit (Non-Statutory Requests)
                </Typography>
                <Typography variant="body2">
                  If a customer requests a cancellation or returns outside the statutory 7-day window, or for an item that does not meet standard return criteria, iWaves IT Solution Services is under no legal obligation to issue a cash refund.
                </Typography>
                <Typography variant="body2">
                  However, at the sole discretion of Management, iWaves IT Solution Services may offer a goodwill settlement consisting of a Digital Coupon Code equal to up to 50% of the actual purchase value. This coupon:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Is valid solely for digital services, extensions, or secondary purchases across our platforms.</li>
                  <li>Is non-transferable and cannot be exchanged, redeemed, or converted into cash or bank transfer under any circumstances.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  2. Digital Services, Annual Renewal &amp; Account Deletion Policy
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  A. Annual Billing Model Exclusivity
                </Typography>
                <Typography variant="body2">
                  Platform access, masked VoIP routing, and dynamic QR features are provided exclusively on a prepaid Annual Subscription basis. Monthly subscription options are not available.
                </Typography>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  B. Non-Storage of Payment Card Credentials &amp; Manual Renewal
                </Typography>
                <Typography variant="body2">
                  In strict compliance with regional financial data protection rules, Qatar Central Bank (QCB) regulations, and PCI-DSS standards:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Neither Rahl nor iWaves IT Solution Services stores, retains, or processes credit card, debit card, or banking credentials on Rahl servers.</li>
                  <li>Initial purchases made via partner portals or the Rahl App are processed through secure, third-party licensed payment gateways.</li>
                  <li>Because card details are never stored, automatic auto-debiting is disabled. Annual renewals must be initiated and paid manually by the Account Holder directly through the Rahl App upon expiration of the annual cycle.</li>
                </Box>
                <Typography variant="h6" component="h4" sx={{ fontSize: '0.95rem !important' }}>
                  C. Account Deletion &amp; No-Refund Rule
                </Typography>
                <Typography variant="body2">
                  Users have the right to request profile deletion or account termination at any time via the Rahl App or Account Dashboard. However:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>Account deletion or early profile termination shall NOT entitle the user to any full, partial, or prorated cash refunds, bank chargebacks, or store credit for the remaining unused months or days of the active annual subscription period.</li>
                  <li>Upon profile deletion, the associated Smart Safety Shield dynamic QR tag will be immediately deactivated and unlinked from the system for security purposes.</li>
                </Box>
                <Box component="ol" sx={{ pl: 3 }}>
                  <li><strong>Digital Service Activation:</strong> Digital activation fees and recurring platform management subscriptions associated with the Rahl Masked Routing System are non-refundable once activated.</li>
                  <li><strong>Subscription Cancellations:</strong> Users may cancel their recurring subscription auto-renewal at any time via their Rahl Account Dashboard. Cancellation will take effect at the end of the current paid billing cycle. No partial or prorated refunds are issued for unused days during an active billing cycle.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  3. Defective or Damaged Products
                </Typography>
                <Typography variant="body2">
                  If a customer receives a product that is physically damaged upon delivery or contains a defective/unscannable QR code prior to application:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li>The customer must notify the Delivery Partner or Rahl Customer Support at support@rahl.me or +974 31395694 within forty-eight (48) hours of delivery, providing photo/video evidence.</li>
                  <li>Upon verification, iWaves IT Solution Services or the Delivery Partner will issue a free replacement product.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  4. Refund Processing Method
                </Typography>
                <Typography variant="body2">
                  Approved monetary refunds will be processed using the original payment method used during purchase:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <li><strong>Credit/Debit Card Payments:</strong> Processed back to the issuing card within 7–14 business days (subject to bank processing times).</li>
                  <li><strong>Cash on Delivery (COD):</strong> Refunded via bank transfer or digital voucher code as agreed with the customer.</li>
                  <li><em>Note:</em> Shipping, delivery, and handling charges are non-refundable unless the return is due to a verified manufacturing defect or fulfillment error by iWaves IT Solution Services.</li>
                </Box>

                <Typography variant="h6" component="h3">
                  5. Customer Service Contact for Claims
                </Typography>
                <Typography variant="body2">
                  For all legal notices, privacy inquiries, or refund claims, contact:
                  <br />
                  <strong>iWaves IT Solution Services – Rahl Support</strong>
                  <br />
                  <strong>Email:</strong> <MuiLink href="mailto:support@rahl.me" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>support@rahl.me</MuiLink>
                  <br />
                  <strong>Customer Care:</strong> <MuiLink href="tel:+97431395694" sx={{ color: '#E9AE31', textDecoration: 'none', fontWeight: 600 }}>+974 31395694</MuiLink>
                  <br />
                  <strong>Address:</strong> Doha, State of Qatar
                  <br />
                  <strong>Web:</strong> www.rahl.me
                </Typography>
              </>
            )}

          </Box>
        </Container>
      </Box>
      <HomeFooter />
    </Box>
  );
}
